import { envId } from '../../config/amplifyOutputs';

const GCS_IMAGE_REGEX = /https:\/\/storage\.googleapis\.com\/mcp-temporary-files\/[^\s"'\]>]+\.(?:png|jpg|jpeg|gif|webp)/gi;
const MARKDOWN_IMAGE_REGEX = /!\[([^\]]*)\]\(([^)]+)\)/g;
// [\s\S] not `.` — see the matching note on GRAPH_TAG_REGEX in graphUtils.ts.
const OUTIMG_TAG_REGEX = /<outimg>([\s\S]*?)<\/outimg>/gi;
// Gate on a real raster extension so a non-image like `![Chart](output/x.json)` never becomes an <img>.
const IMAGE_EXT_REGEX = /\.(?:png|jpe?g|gif|webp)(?:\?|#|$)/i;

function isAllowedAbsoluteUrl(uri: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(uri);
  } catch {
    return false; // not an absolute URL
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  const host = parsed.hostname;
  return host.endsWith('.googleapis.com') || host.endsWith('.amazonaws.com');
}

const CONVERSATION_FOLDERS = ['output', 'input'] as const;

export function toPublicUrl(uri: string, conversationId?: string): string {
  // Relative conversation-folder paths. Tools emit `output/...` for files they write and the
  // model may point a chart/map at an attachment as `input/...` (e.g. a chart spec whose
  // data.csv_uri is the uploaded CSV). Both folders are public-read under the conversation
  // prefix. Anything else relative would fall through to fetch('input/x') against the app
  // origin, which answers with index.html and a 200 — an empty chart with no error.
  const folder = CONVERSATION_FOLDERS.find((p) => uri.startsWith(`${p}/`));
  if (folder) {
    if (conversationId) {
      const filename = uri.slice(folder.length + 1);
      // Construct the full GCS URL using the environment ID from config
      return `https://storage.googleapis.com/mcp-temporary-files/${envId}/chat/${conversationId}/${folder}/${filename}`;
    }
    // Without conversationId, we can't construct the full URL
    console.warn('Cannot construct full URL for:', uri, 'Missing conversationId');
    return uri;
  }
  
  // Convert gs:// URLs to https:// URLs
  if (uri.startsWith('gs://')) {
    const url = uri.replace('gs://', 'https://storage.googleapis.com/');
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    const basePath = parts.slice(0, -1).join('/');
    return basePath + '/' + encodeURIComponent(decodeURIComponent(filename));
  }
  
  let parsed: URL | null = null;
  try {
    parsed = new URL(uri);
  } catch {
    parsed = null;
  }

  const isHttp = parsed !== null && (parsed.protocol === 'https:' || parsed.protocol === 'http:');
  if (parsed && isHttp && parsed.hostname === 'storage.googleapis.com') {
    const parts = uri.split('/');
    const filename = parts[parts.length - 1];
    const basePath = parts.slice(0, -1).join('/');
    return basePath + '/' + encodeURIComponent(decodeURIComponent(filename));
  }

  if (parsed) {
    if (!isAllowedAbsoluteUrl(uri)) {
      // Empty string makes callers fetch('') (the current page) and fail
      // confusingly; warn so a blocked absolute URL is diagnosable.
      console.warn('[toPublicUrl] Disallowed absolute URL blocked:', uri);
      return '';
    }
    return uri;
  }
  return uri;
}

// GCS edge-caches public objects. Any object first fetched before the bucket's
// CORS policy existed is served from cache WITHOUT an Access-Control-Allow-Origin
// header, so the browser blocks the fetch even though the bucket is now
// CORS-enabled. A stable query param gives those a fresh cache key that GCS
// re-evaluates with CORS, while still letting the per-conversation fetch caches
// hit once per file. NOTE: `cors` is a reserved GCS query parameter (returns 400)
// — use an innocuous key like `cb`. (Durable fix lives backend-side: write these
// ephemeral files with Cache-Control: no-store so nothing caches a header-less
// copy.)
//
// Scope it to GCS only: shared-conversation artifacts resolve to S3 presigned
// URLs whose signature covers the query string, so appending `cb` would
// invalidate them. Non-GCS URLs are returned unchanged.
export const withCacheBuster = (url: string): string =>
  url.includes('storage.googleapis.com')
    ? url + (url.includes('?') ? '&' : '?') + 'cb=1'
    : url;

export function extractImagesFromMessage(message: string, conversationId?: string): { urls: string[]; originalMessage: string } | null {
  const urls: string[] = [];
  // Generated artifacts removed from the prose but not displayable (e.g. a chart's .json spec) —
  // tracked so the cleaned text is still returned when the message yielded no actual image.
  let strippedNonImages = false;

  // IMPORTANT: extract with a single replace()-with-callback pass per pattern. Do NOT drive a
  // stateful global regex with exec() while mutating the string inside the loop — after a long
  // match is removed, the regex's lastIndex points past later tags and silently drops the 2nd+
  // image. This bites the shared viewer in particular, where <outimg> tags carry ~800-char
  // presigned URLs, so any message with 2+ images would lose all but the first.

  // Extract Markdown images first (only output/ or GCS-hosted; leave other markdown intact).
  let processedMessage = message.replace(MARKDOWN_IMAGE_REGEX, (full, _alt, imagePath) => {
    // Not a generated artifact — leave the author's own markdown alone.
    if (!imagePath.startsWith('output/') && !imagePath.includes('storage.googleapis.com')) return full;
    // Ours either way: MarkdownMessage has no image renderer, so anything left behind shows as "!" plus a dead link.
    if (IMAGE_EXT_REGEX.test(imagePath)) urls.push(imagePath);
    else strippedNonImages = true;
    return '';
  });

  // Extract <outimg> tags. Same extension gate as the markdown branch above — a chart-spec
  // `.json` inside a (mis-tagged) <outimg> must strip cleanly, not render as a broken <img>.
  processedMessage = processedMessage.replace(OUTIMG_TAG_REGEX, (_full, inner) => {
    const path = inner.trim();
    if (IMAGE_EXT_REGEX.test(path)) urls.push(path);
    else strippedNonImages = true;
    return '';
  });

  // Extract raw GCS URLs
  const gcsMatches = processedMessage.match(GCS_IMAGE_REGEX);
  if (gcsMatches) {
    urls.push(...gcsMatches);
    processedMessage = processedMessage.replace(GCS_IMAGE_REGEX, '');
  }

  if (urls.length === 0 && !strippedNonImages) return null;

  return {
    urls,
    originalMessage: processedMessage.trim(),
  };
}
